import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

// ────────────────────────────────────────────────────────────────
// Shared layout for legal pages
// ────────────────────────────────────────────────────────────────
function LegalLayout({ title, lead, lastUpdated, children, testId }) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="App">
      <Navigation />
      <main
        data-testid={testId}
        className="pt-32 pb-20 bg-[var(--brand-parchment)]"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="link-expand inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest)] mb-10"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="block">
            <span className="font-eyebrow">Legal</span>
          </div>
          <h1 className="mt-3 font-display text-5xl lg:text-6xl text-[var(--brand-forest)] tracking-tight leading-[1.02]">
            {title}
          </h1>
          {lead && (
            <p className="mt-5 text-[var(--brand-muted)] text-lg leading-relaxed">{lead}</p>
          )}
          <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)]">
            Last updated: {lastUpdated}
          </p>
          <div className="legal-body mt-12 space-y-8 text-[var(--brand-ink)] leading-[1.75]">
            {children}
          </div>
          <div className="mt-16 pt-8 border-t border-[var(--brand-stone)] text-sm text-[var(--brand-muted)]">
            Questions about this policy? Drop us a note at{' '}
            <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
              hello@ecobikecampingsuppliesco.com
            </a>
            .
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl lg:text-3xl text-[var(--brand-forest)] tracking-tight mt-12 mb-3 first:mt-0">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-xl text-[var(--brand-forest)] mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="text-base leading-[1.8]">{children}</p>;
const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 marker:text-[var(--brand-olive)]">{children}</ul>
);
const STRONG = ({ children }) => (
  <strong className="font-semibold text-[var(--brand-forest)]">{children}</strong>
);

// ────────────────────────────────────────────────────────────────
// Shipping & Delivery
// ────────────────────────────────────────────────────────────────
export function ShippingPolicy() {
  return (
    <LegalLayout
      title="Shipping & Delivery"
      lead="Free standard delivery on every order — no minimum, no thresholds, no small print."
      lastUpdated="1 March 2026"
      testId="shipping-policy-page"
    >
      <section data-testid="shipping-free-uk">
        <div className="bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] px-6 py-8 mb-10">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[var(--brand-gold)] mb-2">
            The promise
          </div>
          <div className="font-display text-3xl lg:text-4xl leading-tight tracking-tight">
            Free standard delivery on all orders to mainland UK.
          </div>
          <p className="mt-4 text-sm opacity-90 max-w-xl leading-relaxed">
            Every order ships free. A £12.95 toggle, a £74.95 kit, a £1,799
            e-bike &mdash; the postage doesn&rsquo;t change. It&rsquo;s the
            standard we&rsquo;d want as customers ourselves.
          </p>
        </div>
      </section>

      <section>
        <H2>Where we deliver</H2>
        <UL>
          <li>
            <STRONG>Mainland UK</STRONG>: free standard delivery, every order.
          </li>
          <li>
            <STRONG>Scottish Highlands &amp; Islands</STRONG>: free delivery,
            please allow <STRONG>1&ndash;2 extra working days</STRONG> for the
            longer haul out to the postcodes above the Great Glen and across
            the water.
          </li>
          <li>
            <STRONG>Channel Islands, Isle of Man, Northern Ireland</STRONG>:
            free delivery, please allow <STRONG>1&ndash;2 extra working days</STRONG>.
          </li>
          <li>
            <STRONG>International</STRONG>: available on request &mdash; drop us
            a note at{' '}
            <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
              hello@ecobikecampingsuppliesco.com
            </a>{' '}
            with your country and we&rsquo;ll come back with a quote and a
            realistic timeline.
          </li>
        </UL>
      </section>

      <section>
        <H2>Delivery timeframes</H2>
        <P>
          Because we drop-ship straight from our manufacturing partners rather
          than sitting on warehouse stock, lead times split into two brackets
          depending on where your kit is coming from:
        </P>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div
            data-testid="shipping-timeframe-uk"
            className="bg-[var(--brand-parchment)] border border-[var(--brand-stone)] border-l-4 border-l-[var(--brand-gold)] p-5"
          >
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[var(--brand-forest-deep)] mb-2">
              UK stock
            </div>
            <div className="font-display text-2xl text-[var(--brand-forest)] leading-tight">
              2&ndash;4 working days
            </div>
            <p className="mt-2 text-sm text-[var(--brand-ink)] leading-relaxed">
              Fire steels, tinder kits, field knives and other Heritage Estate
              items dispatched from our UK-import warehouse.
            </p>
          </div>
          <div
            data-testid="shipping-timeframe-international"
            className="bg-[var(--brand-parchment)] border border-[var(--brand-stone)] border-l-4 border-l-[var(--brand-olive)] p-5"
          >
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[var(--brand-forest-deep)] mb-2">
              International stock
            </div>
            <div className="font-display text-2xl text-[var(--brand-forest)] leading-tight">
              2&ndash;4 weeks
            </div>
            <p className="mt-2 text-sm text-[var(--brand-ink)] leading-relaxed">
              E-bikes and larger kit dispatched direct from the manufacturer
              overseas. Customs clearance is included in the timeline; you pay
              nothing extra on arrival.
            </p>
          </div>
        </div>
        <P>
          The bracket that applies to your order is shown on each product page
          and again at checkout, so there are no surprises. Working days are
          Monday&ndash;Friday excluding UK bank holidays.
        </P>
      </section>

      <section>
        <H2>Tracking your order</H2>
        <P>
          You&rsquo;ll receive a dispatch confirmation email with tracking as
          soon as the courier collects. If anything looks wrong at your end
          &mdash; wrong address, need to reroute to a neighbour, no-one home
          &mdash; drop us a note at{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>{' '}
          and we&rsquo;ll sort it before it ships.
        </P>
      </section>

      <section>
        <H2>Anything else</H2>
        <P>
          Risk in the goods passes to you on delivery. Damaged in transit?
          Photograph the packaging before you sign, keep the box, and email us
          the same day &mdash; the carrier claim runs cleanest that way. We
          cover the replacement.
        </P>
        <P>
          Returns and cancellations are covered separately under our{' '}
          <Link to="/conditions-of-sale" className="link-expand">
            Conditions of Sale
          </Link>{' '}
          (14-day cooling-off period under UK consumer law).
        </P>
      </section>
    </LegalLayout>
  );
}

// ────────────────────────────────────────────────────────────────
// Privacy Policy
// ────────────────────────────────────────────────────────────────
export function PrivacyPolicy() {
  return (
    <LegalLayout
      testId="privacy-page"
      title="Privacy Policy"
      lastUpdated="15 January 2026"
      lead="Plain-English explanation of what personal information we collect, why we collect it, and the rights you have over it under UK GDPR and the Data Protection Act 2018."
    >
      <section>
        <H2>1. Who we are</H2>
        <P>
          EcoBike Camping Supplies Co. (“we”, “us”, “our”) is a sole-trader heritage outdoors
          retailer based on the west coast of Scotland (Ayrshire, United Kingdom). We
          operate online only at <STRONG>ecobikecampingsuppliesco.com</STRONG> and{' '}
          <STRONG>ecobikecampingsuppliesco.co.uk</STRONG>. We are the “data controller” for
          personal information collected through these sites.
        </P>
        <P>
          To contact us about any privacy matter, email{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>
          .
        </P>
      </section>

      <section>
        <H2>2. What information we collect</H2>
        <H3>2.1 Enquiry form</H3>
        <P>
          When you submit our contact / enquiry form, we collect: your full name,
          email address, optional phone number, the subject of your enquiry, the
          category of interest you select (e.g. e-bikes, camping, adaptive), and
          the message you send us.
        </P>
        <H3>2.2 Newsletter signup</H3>
        <P>
          When you join Field Notes (our newsletter), we collect your email
          address and the source page from which you signed up.
        </P>
        <H3>2.3 Technical information</H3>
        <P>
          Like most websites, our hosting provider logs basic technical
          information (IP address, browser type, pages visited) for the purpose
          of security and operational performance. We do not use these logs for
          marketing.
        </P>
        <H3>2.4 Cookies</H3>
        <P>
          We use a single essential cookie to remember your cookie-preference
          choice. We do not currently use analytics, advertising, or tracking
          cookies. Should that ever change, we will update this policy and the
          cookie banner before any such cookies are set.
        </P>
      </section>

      <section>
        <H2>3. Why we collect it and our lawful basis</H2>
        <UL>
          <li>
            <STRONG>To respond to your enquiry</STRONG> — lawful basis: legitimate interest
            and, where applicable, taking steps prior to entering into a contract.
          </li>
          <li>
            <STRONG>To send you the Field Notes newsletter</STRONG> — lawful basis:
            consent (you opt in by submitting your email). You may withdraw consent
            at any time.
          </li>
          <li>
            <STRONG>To operate and secure the website</STRONG> — lawful basis:
            legitimate interest in providing a working service.
          </li>
        </UL>
      </section>

      <section>
        <H2>4. Who we share it with</H2>
        <P>
          We do not sell your personal information to anyone. We share limited
          information only with the following processors that help us run the
          business:
        </P>
        <UL>
          <li>
            <STRONG>Hosting & domain provider:</STRONG> Fasthosts Internet Ltd
            (UK-based) for domain registration and DNS.
          </li>
          <li>
            <STRONG>Application hosting:</STRONG> Emergent (cloud infrastructure that
            stores enquiry and subscriber records in our database).
          </li>
          <li>
            <STRONG>Email delivery (future):</STRONG> When we begin sending newsletters,
            we will use a UK/EU-compliant email service provider and will list it
            here before any emails are sent.
          </li>
        </UL>
        <P>
          Where any processor is located outside the UK/EEA, we ensure adequate
          safeguards (Standard Contractual Clauses or an adequacy decision) are
          in place.
        </P>
      </section>

      <section>
        <H2>5. How long we keep it</H2>
        <UL>
          <li>
            <STRONG>Enquiries:</STRONG> up to 24 months from your last contact, then
            deleted.
          </li>
          <li>
            <STRONG>Newsletter signups:</STRONG> until you unsubscribe (every email
            we send will include a one-click unsubscribe link).
          </li>
          <li>
            <STRONG>Server logs:</STRONG> a maximum of 90 days, then deleted.
          </li>
        </UL>
      </section>

      <section>
        <H2>6. Your rights under UK GDPR</H2>
        <P>You have the right to:</P>
        <UL>
          <li>be told what we hold about you and ask for a copy (right of access);</li>
          <li>have inaccurate data corrected (right to rectification);</li>
          <li>have your data deleted in certain circumstances (right to erasure);</li>
          <li>restrict how we use it (right to restriction);</li>
          <li>object to certain types of processing (right to object);</li>
          <li>receive your data in a portable format (right to portability);</li>
          <li>withdraw consent at any time, where consent is the lawful basis.</li>
        </UL>
        <P>
          To exercise any of these rights, email{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>
          . We will respond within one calendar month.
        </P>
      </section>

      <section>
        <H2>7. Complaints</H2>
        <P>
          If you are not satisfied with how we have handled your personal
          information, you have the right to complain to the UK Information
          Commissioner&rsquo;s Office (ICO): <a className="link-expand" href="https://ico.org.uk">ico.org.uk</a>,
          0303 123 1113.
        </P>
      </section>

      <section>
        <H2>8. Changes to this policy</H2>
        <P>
          We may update this policy from time to time. Material changes will be
          highlighted on this page and dated in the “Last updated” line at the top.
        </P>
      </section>
    </LegalLayout>
  );
}

// ────────────────────────────────────────────────────────────────
// Terms & Conditions
// ────────────────────────────────────────────────────────────────
export function TermsAndConditions() {
  return (
    <LegalLayout
      testId="terms-page"
      title="Terms of Service"
      lastUpdated="28 February 2026"
      lead="The terms on which we offer this website and, once orders open, the framework contract between us when you buy from us. For the detailed payment, delivery and return terms please see our separate Conditions of Sale."
    >
      <section>
        <div className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] p-5 -mt-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)] mb-2">
            Product misuse &amp; liability
          </div>
          <p className="text-[15px] leading-relaxed text-[var(--brand-ink)]">
            Eco-Bikes &amp; Camping Supplies Co. shall not be held responsible
            for any misuse, illegal operation, or modification of products once
            they have left our workshop. Users operate all equipment at their
            own risk and are responsible for adhering to local laws and safety
            guidelines.
          </p>
        </div>
      </section>
      <section>
        <H2>1. About us</H2>
        <P>
          This website is operated by EcoBike Camping Supplies Co., a sole-trader
          business based in Ayrshire, Scotland, United Kingdom (“we”, “us”). You
          can reach us at{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>
          .
        </P>
      </section>

      <section>
        <H2>2. Using this website</H2>
        <P>
          By using this website you accept these terms. If you do not accept them,
          please do not use the site. We may update these terms from time to time
          — the version in force is the one shown at the top of this page on the
          date you submit an enquiry or place an order.
        </P>
      </section>

      <section>
        <H2>3. Enquiries vs. orders</H2>
        <P>
          At present this website is a <STRONG>catalogue and enquiry site only</STRONG>.
          Submitting an enquiry through our contact form does not create a contract
          between us. It is a request for information; we will reply to discuss
          availability, suitability and price before any sale is agreed.
        </P>
        <P>
          When we begin taking direct orders, the additional terms in section 7
          will apply.
        </P>
      </section>

      <section>
        <H2>4. Product information</H2>
        <P>
          We take care to describe our products accurately, but the catalogue is
          updated periodically and minor specification changes may occur. Product
          images are illustrative; small variations in colour or finish are normal
          for handmade and naturally-occurring materials such as waxed cotton or
          ash wood.
        </P>
      </section>

      <section>
        <H2>5. UK road-legal e-bikes (EAPC compliance)</H2>
        <P>
          All electric bicycles listed on our site are sold as UK “Electrically
          Assisted Pedal Cycles” (EAPCs) as defined by the{' '}
          <em>Electrically Assisted Pedal Cycles Regulations 1983</em> and current
          guidance from the Department for Transport. This means:
        </P>
        <UL>
          <li>maximum continuous motor power: 250W;</li>
          <li>electrical assistance cuts out at 15.5 mph (25 km/h);</li>
          <li>pedal-assist only above 4 mph;</li>
          <li>rider must be 14 years old or over.</li>
        </UL>
        <P>
          EAPCs do not require a driving licence, road tax, vehicle registration
          or compulsory insurance under UK law, but we still recommend you arrange
          third-party cycle insurance and wear an approved helmet.
        </P>
        <P>
          Any bike that does not meet these specifications is not road-legal in
          the UK without registration as a motor vehicle and we will not knowingly
          sell such a bike for road use.
        </P>
      </section>

      <section>
        <H2>6. Intellectual property</H2>
        <P>
          The EcoBike Camping Supplies Co. name, the Ben Lomond medallion, all
          text, photographs and design elements on this website are our property
          (or used under licence) and protected by copyright and trade-mark law.
          You may not copy or reproduce them for commercial use without our
          written permission.
        </P>
      </section>

      <section>
        <H2>6a. Age restrictions</H2>
        <P>
          Certain products in our catalogue may only be purchased by adults:
        </P>
        <UL>
          <li>
            <STRONG>Bladed articles</STRONG> — knives, multi-tools with a locking
            blade, axes and other bladed bushcraft tools may only be purchased
            by persons aged <STRONG>18 or over</STRONG> in line with the
            Criminal Justice Act 1988 and the Offensive Weapons Act 2019.
          </li>
          <li>
            <STRONG>E-bikes</STRONG> — Electrically Assisted Pedal Cycles may be
            ridden by anyone aged 14 or over on the public highway; parents
            and guardians are responsible for supervising younger riders.
          </li>
        </UL>
        <P>
          At checkout you will be asked to confirm you are of the required age
          for any item that carries an age restriction. Providing false age
          information is a criminal offence.
        </P>
      </section>

      <section>
        <H2>6b. Right to refuse service</H2>
        <P>
          We reserve the right to refuse, cancel or limit any order at our sole
          discretion, including &mdash; without limitation &mdash; where:
        </P>
        <UL>
          <li>we have reasonable grounds to believe an age-restricted item is being purchased by, or for, someone under the minimum age;</li>
          <li>a delivery address or billing detail cannot be verified;</li>
          <li>a product was listed at a manifestly incorrect price;</li>
          <li>fulfilment would breach any UK law or the manufacturer&rsquo;s import restrictions;</li>
          <li>we reasonably suspect the intended use is unlawful or dangerous.</li>
        </UL>
        <P>
          Where we refuse or cancel an order after payment, we will refund the
          full amount to the original payment method within 14 days.
        </P>
      </section>

      <section>
        <H2>7. When orders open (future)</H2>
        <H3>7.1 Forming a contract</H3>
        <P>
          When direct orders open, placing an order through our website will
          constitute an offer. A binding contract is formed only when we send a
          dispatch confirmation email. Until then we may decline an order (for
          example if a product is out of stock or the price was listed in error).
        </P>
        <H3>7.2 Prices and payment</H3>
        <P>
          Prices are shown in pounds sterling (£) and include UK VAT where
          applicable. We accept payment via the payment providers listed at
          checkout. Delivery charges, if any, are shown before you confirm your
          order.
        </P>
        <H3>7.3 Your right to cancel (distance selling)</H3>
        <P>
          Under the Consumer Contracts (Information, Cancellation and Additional
          Charges) Regulations 2013, you have <STRONG>14 days from the day you receive
          your goods</STRONG> to change your mind. To exercise this right, email us at{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>{' '}
          stating you wish to cancel. You then have a further 14 days to return
          the goods in unused condition. Return shipping is at your cost unless
          the goods were faulty or wrongly described.
        </P>
        <P>
          Exception: <em>bespoke or custom-finished items</em> (for example, bikes
          with custom adaptive modifications made to your specification) are not
          eligible for the 14-day cooling-off period once production has started.
        </P>
        <H3>7.4 Faulty goods (Consumer Rights Act 2015)</H3>
        <P>
          We are required by law to provide goods of satisfactory quality, fit
          for purpose and as described. If something is faulty:
        </P>
        <UL>
          <li>
            <STRONG>Within 30 days:</STRONG> you have the right to a full refund.
          </li>
          <li>
            <STRONG>Within 6 months:</STRONG> you are entitled to repair, replacement
            or refund (faults within this period are presumed to be present at
            delivery unless we can show otherwise).
          </li>
          <li>
            <STRONG>After 6 months and up to 6 years:</STRONG> you may still be
            entitled to a remedy if the fault relates to the durability of the
            goods.
          </li>
        </UL>
      </section>

      <section>
        <H2>8. Limitation of liability</H2>
        <P>
          Nothing in these terms limits or excludes our liability for death or
          personal injury caused by our negligence, for fraud, or for any other
          liability that cannot be excluded under UK law. Subject to that, our
          total liability to you for any single order shall not exceed the price
          you paid for that order.
        </P>
        <P>
          We are not liable for any indirect or consequential losses (for example,
          loss of profit, loss of opportunity), nor for any damage caused by the
          misuse of a product against its instructions.
        </P>
      </section>

      <section>
        <H2>9. Governing law</H2>
        <P>
          These terms are governed by the laws of <STRONG>Scotland</STRONG> and any
          dispute will be subject to the exclusive jurisdiction of the Scottish
          courts.
        </P>
      </section>

      <section>
        <H2>10. Contact</H2>
        <P>
          For any question about these terms or an order, email{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>
          .
        </P>
      </section>
    </LegalLayout>
  );
}

// ────────────────────────────────────────────────────────────────
// Compliance & UK E-Bike Regulations
// ────────────────────────────────────────────────────────────────
export function CompliancePolicy() {
  return (
    <LegalLayout
      testId="compliance-page"
      title="Compliance"
      lastUpdated="15 July 2026"
      lead="Our public commitment to selling only UK-legal e-bikes, and where we stand on illegal modification kits, hidden power-unlocks, and cooperation with Trading Standards and the police."
    >
      <section>
        <H2>1. Our position, plainly</H2>
        <P>
          EcoBike Camping Supplies Co. exists to sell honest, road-legal
          equipment to UK customers &mdash; not to skirt regulations. Every
          electric bicycle we import is sold as a UK{' '}
          <STRONG>Electrically Assisted Pedal Cycle (EAPC)</STRONG> conforming to
          the Electrically Assisted Pedal Cycles Regulations 1983 and the
          European standard EN 15194.
        </P>
        <P>
          We are aware that a growing minority of UK e-bike sellers push
          non-compliant hardware, hidden-unlock firmware and after-market
          &ldquo;derestriction&rdquo; kits. We do not, and will not, be one of
          them. We would rather lose the sale than risk a customer&rsquo;s safety,
          a rider&rsquo;s legal position, or the reputation of the trade.
        </P>
      </section>

      <section>
        <H2>2. The UK-legal spec, in one paragraph</H2>
        <UL>
          <li>Motor: <STRONG>250 W continuous</STRONG> maximum &mdash; not 500 W, not 750 W.</li>
          <li>Electric assistance <STRONG>cuts out at 15.5 mph (25 km/h)</STRONG>.</li>
          <li><STRONG>Pedal-assist only</STRONG> above 4 mph; throttles are permitted only as walk-assist below 4 mph.</li>
          <li>Rider must be aged 14 or over.</li>
          <li>Frame markings showing motor power and manufacturer.</li>
          <li>
            <STRONG>CE and/or UKCA marking</STRONG>, plus{' '}
            <STRONG>EN 15194 conformity certificate</STRONG> supplied per shipment.
          </li>
        </UL>
        <P>
          A bike that meets all six requirements does <STRONG>not</STRONG> need
          registration, road tax, an MOT, a driving licence or compulsory
          insurance under UK law. A bike that falls short of any one of them
          requires all of the above &mdash; and cannot legally be sold as an
          EAPC.
        </P>
      </section>

      <section>
        <H2>3. What we refuse to sell</H2>
        <P>
          We do not stock or supply:
        </P>
        <UL>
          <li>
            <STRONG>Motors above 250 W continuous.</STRONG> Not the 500 W, 750 W
            or 1,000 W export variants many Asian factories ship as standard.
          </li>
          <li>
            <STRONG>Bikes with hidden speed-unlock or region-switch modes.</STRONG>{' '}
            Every bike we source is subject to a written assurance from the
            manufacturer that no button-combination, app connection or firmware
            option exists to raise the assist cut-off beyond 15.5 mph.
          </li>
          <li>
            <STRONG>Throttles operational above 4 mph.</STRONG> Twist-grip or
            thumb throttles that allow the bike to move under its own power
            without the rider pedalling are not permitted under UK EAPC rules.
          </li>
          <li>
            <STRONG>De-restriction kits or firmware.</STRONG> We do not sell or
            recommend any modification designed to bypass EAPC limits.
          </li>
          <li>
            <STRONG>Uncertified batteries or chargers.</STRONG> All lithium
            packs we supply meet UN 38.3 for transport and are supplied with
            RoHS and CE/UKCA documentation.
          </li>
        </UL>
      </section>

      <section>
        <H2>4. What we do at source</H2>
        <P>
          Because we operate a drop-ship model with our manufacturing partners,
          compliance is enforced at the factory rather than in a UK warehouse.
          Every purchase order we place requires:
        </P>
        <UL>
          <li>Written confirmation that the unit is the <STRONG>UK-EAPC 250 W variant</STRONG>, permanently locked in firmware.</li>
          <li>CE / UKCA plate and marking present on the frame.</li>
          <li>EN 15194 conformity certificate supplied in each bike&rsquo;s manual sleeve.</li>
          <li>Commercial invoice with the correct customs classification (HS 8711.60).</li>
          <li>Battery documentation (UN 38.3 transport certificate, RoHS statement).</li>
        </UL>
        <P>
          Any shipment that arrives without this documentation is refused at
          the customer&rsquo;s address and returned to the manufacturer at their
          cost, at no charge to the customer.
        </P>
      </section>

      <section>
        <H2>5. Cooperation with Trading Standards &amp; police</H2>
        <P>
          We keep records of the CE/UKCA certificates, conformity documents
          and firmware assurances for every unit sold, for a minimum of{' '}
          <STRONG>six years</STRONG> from the date of sale. In the event of an
          enquiry from Trading Standards, the Office for Product Safety and
          Standards, or a police force pursuing enforcement action, we will
          provide these records promptly and in full.
        </P>
        <P>
          If we ever become aware that a batch of bikes we have supplied fails
          to meet EAPC standards &mdash; for example, through a firmware bug
          or a manufacturer changing specification without notice &mdash; we
          will:
        </P>
        <UL>
          <li>Publish a notice on this page within 48 hours of confirmation.</li>
          <li>Contact every customer of that batch by email and (if provided) SMS.</li>
          <li>Offer a full refund, uplift and disposal at our cost.</li>
        </UL>
      </section>

      <section>
        <H2>6. The rider&rsquo;s responsibilities</H2>
        <P>
          Selling a UK-legal bike is our commitment. Keeping it that way is
          also the rider&rsquo;s. If you modify a bike we&rsquo;ve sold you
          &mdash; for example by fitting an after-market motor, changing the
          controller, or installing derestriction firmware &mdash; the bike is
          no longer EAPC-compliant and may not be ridden on the road, cycle
          paths or bridleways without registering it as a motor vehicle.
        </P>
        <P>
          We will not knowingly assist with any such modification, and our
          warranty is void from the moment any change of that kind is made.
        </P>
      </section>

      <section>
        <H2>7. Get in touch</H2>
        <P>
          Enforcement enquiries, compliance verification or general questions
          about the standards on this page: email{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>{' '}
          with subject line &ldquo;Compliance&rdquo; and we will respond within
          one working day.
        </P>
        <P>
          For the current government position on UK EAPC rules, see{' '}
          <a
            className="link-expand"
            href="https://www.gov.uk/electric-bike-rules"
            target="_blank"
            rel="noopener noreferrer"
          >
            gov.uk/electric-bike-rules
          </a>
          .
        </P>
      </section>
    </LegalLayout>
  );
}
export function CookiePolicy() {
  return (
    <LegalLayout
      testId="cookies-page"
      title="Cookies Policy"
      lastUpdated="15 January 2026"
      lead="Plain English on what cookies this website uses and how you can control them."
    >
      <section>
        <H2>What are cookies?</H2>
        <P>
          Cookies are small text files that websites place on your device to
          remember things between page loads or visits. Some are essential to a
          working website; others are used for analytics or advertising.
        </P>
      </section>

      <section>
        <H2>What we use</H2>
        <P>
          We currently use a <STRONG>single essential cookie</STRONG>:
        </P>
        <UL>
          <li>
            <STRONG>ecobike_cookie_consent</STRONG> — remembers your choice on the
            cookie banner so we do not show it on every page load. Set for 12
            months. No personal information stored.
          </li>
        </UL>
        <P>
          We do <STRONG>not</STRONG> currently use analytics cookies (e.g. Google
          Analytics), advertising cookies (e.g. Meta Pixel) or any third-party
          tracking cookies. If we add any of those in future, the cookie banner
          and this page will be updated first.
        </P>
      </section>

      <section>
        <H2>Managing cookies</H2>
        <P>
          You can clear or block cookies in your browser settings at any time. For
          guidance see{' '}
          <a className="link-expand" href="https://www.aboutcookies.org/" target="_blank" rel="noopener noreferrer">
            aboutcookies.org
          </a>
          .
        </P>
      </section>
    </LegalLayout>
  );
}

// ────────────────────────────────────────────────────────────────
// Conditions of Sale
// ────────────────────────────────────────────────────────────────
export function ConditionsOfSale() {
  return (
    <LegalLayout
      testId="conditions-of-sale-page"
      title="Conditions of Sale"
      lastUpdated="28 February 2026"
      lead="The commercial terms that apply when you buy from us — payment, delivery, returns, warranties and your right to cancel under UK consumer law."
    >
      <section>
        <H2>1. Forming a contract</H2>
        <P>
          Placing an order through our website is an offer to buy. A binding
          contract is formed only when we send a dispatch confirmation email
          for the goods concerned. Until that email is sent we may decline the
          order — for example if a product is out of stock, the price was
          listed in error, or a manufacturer&rsquo;s import restriction
          applies.
        </P>
      </section>

      <section>
        <H2>2. Prices &amp; payment</H2>
        <P>
          Prices are shown in pounds sterling (£) and include UK VAT where
          applicable. We accept the payment methods offered by our payment
          processor (Stripe) at checkout, including debit and credit cards.
          Delivery charges, if any, are shown before you confirm your order.
        </P>
        <P>
          Payment is taken in full at the point of checkout. Your card details
          are handled by Stripe on Stripe&rsquo;s own secure infrastructure —
          we never see or store your card number.
        </P>
      </section>

      <section>
        <H2>3. Product descriptions</H2>
        <P>
          Product descriptions are accurate to the best of our knowledge at the
          time of listing. Specifications, colour finish and small design
          details may change without notice; where a material change occurs
          before your order ships, we will contact you and offer either the
          revised product or a full refund.
        </P>
      </section>

      <section>
        <H2>4. Delivery</H2>
        <P>
          Most items on this catalogue are supplied on a drop-ship basis
          direct from the manufacturer&rsquo;s UK-import warehouse. Typical
          lead times will be shown against each product at checkout. We aim to
          dispatch within the stated lead time but cannot guarantee it where
          delay is caused by the carrier, customs clearance, or events
          outside our reasonable control (severe weather, industrial action,
          etc.). Risk in the goods passes to you on delivery.
        </P>
      </section>

      <section>
        <H2>5. Your right to cancel (14-day cooling-off)</H2>
        <P>
          Under the{' '}
          <em>
            Consumer Contracts (Information, Cancellation and Additional Charges)
            Regulations 2013
          </em>
          , you have{' '}
          <STRONG>14 days from the day you receive your goods</STRONG> to change
          your mind for any reason. To exercise this right, email{' '}
          <a className="link-expand" href="mailto:hello@ecobikecampingsuppliesco.com">
            hello@ecobikecampingsuppliesco.com
          </a>{' '}
          within that period stating that you wish to cancel. You then have a
          further 14 days to return the goods to us in unused, resaleable
          condition. Return shipping is at your cost unless the goods were
          faulty or wrongly described.
        </P>
        <P>
          Exception: <em>bespoke or custom-finished items</em> (for example
          bikes with custom adaptive modifications made to your specification)
          are not eligible for the 14-day cooling-off period once production
          has started.
        </P>
      </section>

      <section>
        <H2>6. Warranty</H2>
        <P>
          Every product we sell carries the <STRONG>manufacturer&rsquo;s original
          warranty</STRONG> in full. The specific term, cover and exclusions are
          set out in the paperwork supplied in the box with each item. Where
          the manufacturer&rsquo;s warranty is administered by us on their
          behalf (as with our Eahora e-bike range), we will act as first point
          of contact for claims and coordinate the repair, replacement or
          refund with the factory at no cost to you.
        </P>
        <P>
          Any modification, misuse, or use of the product outside its intended
          purpose voids the manufacturer&rsquo;s warranty immediately. See our{' '}
          <Link to="/disclaimer" className="link-expand">Disclaimer</Link> page
          for detail on lawful use.
        </P>
      </section>

      <section>
        <H2>7. Faulty goods (Consumer Rights Act 2015)</H2>
        <P>
          Independently of any manufacturer warranty, UK law requires us to
          provide goods of satisfactory quality, fit for purpose and as
          described. If something is faulty:
        </P>
        <UL>
          <li>
            <STRONG>Within 30 days:</STRONG> you have the right to a full refund.
          </li>
          <li>
            <STRONG>Within 6 months:</STRONG> you are entitled to repair,
            replacement or refund — faults within this period are presumed
            present at delivery unless we can show otherwise.
          </li>
          <li>
            <STRONG>Between 6 months and 6 years:</STRONG> you may still be
            entitled to a remedy if the fault relates to the durability of the
            goods.
          </li>
        </UL>
      </section>

      <section>
        <H2>8. Limitation of liability</H2>
        <P>
          Nothing in these conditions limits or excludes our liability for
          death or personal injury caused by our negligence, for fraud, or for
          any other liability that cannot be excluded under UK law. Subject to
          that, our total liability to you for any single order shall not
          exceed the price you paid for that order. We are not liable for any
          indirect or consequential loss (for example loss of profit or loss
          of opportunity) nor for any damage caused by misuse of a product
          against its instructions — please read our{' '}
          <Link to="/disclaimer" className="link-expand">Disclaimer</Link>.
        </P>
      </section>

      <section>
        <H2>9. Governing law</H2>
        <P>
          These conditions are governed by the laws of <STRONG>Scotland</STRONG>{' '}
          and any dispute will be subject to the exclusive jurisdiction of the
          Scottish courts.
        </P>
      </section>
    </LegalLayout>
  );
}

// ────────────────────────────────────────────────────────────────
// Disclaimer
// ────────────────────────────────────────────────────────────────
export function Disclaimer() {
  return (
    <LegalLayout
      testId="disclaimer-page"
      title="Disclaimer"
      lastUpdated="28 February 2026"
      lead="A plain-English note on lawful use, EAPC e-bike compliance, and the law around bladed articles. Not intimidating — just the sober facts every buyer should know before they ride or before they carry."
    >
      <section>
        <div className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] p-5 -mt-2 mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)] mb-2">
            Product misuse &amp; liability
          </div>
          <p className="text-[15px] leading-relaxed text-[var(--brand-ink)]">
            Eco-Bikes &amp; Camping Supplies Co. shall not be held responsible
            for any misuse, illegal operation, or modification of products once
            they have left our workshop. Users operate all equipment at their
            own risk and are responsible for adhering to local laws and safety
            guidelines.
          </p>
        </div>
      </section>

      <section>
        <H2>1. E-Bike Compliance</H2>
        <P>
          All e-bikes sold by Eco-Bikes &amp; Camping Supplies Co. are supplied
          as <STRONG>EAPC-compliant</STRONG> (250 W continuous, pedal-assist,
          maximum 15.5 mph assisted). It is the rider&rsquo;s sole
          responsibility to ensure compliance with{' '}
          <em>The Electrically Assisted Pedal Cycles Regulations 1983 (as
          amended)</em>, to wear appropriate safety equipment, and to obey all
          road traffic laws.
        </P>
        <P>
          Any modification that takes the bike outside EAPC classification —
          for example a swapped-in higher-wattage motor, a controller
          re-flash, a derestriction dongle, or a firmware unlock — renders it
          <STRONG> illegal for road use</STRONG> and{' '}
          <STRONG>voids all warranties</STRONG>. See our{' '}
          <Link to="/compliance" className="link-expand">Compliance</Link>{' '}
          page for the full technical position.
        </P>
      </section>

      <section>
        <H2>2. Tool &amp; Knife Safety</H2>
        <P>
          Knives, fire-starters and bushcraft tools are sold strictly for
          legitimate outdoor, camping and bushcraft use. Buyers must be{' '}
          <STRONG>18 or over</STRONG>. It is the buyer&rsquo;s responsibility
          to comply with the{' '}
          <em>Criminal Justice Act 1988</em>, the{' '}
          <em>Offensive Weapons Act 2019</em> and any local bylaws regarding
          the carry and use of bladed articles.
        </P>
        <P>
          In brief, under current UK law:
        </P>
        <UL>
          <li>It is generally an offence to carry any bladed or sharply-pointed article in a public place without <em>good reason or lawful authority</em>.</li>
          <li>&ldquo;Good reason&rdquo; typically covers work, sport, camping, fishing or transporting a newly-bought knife home in its original packaging.</li>
          <li>Lock-knives, fixed-blade knives and any blade over 3 inches (7.62 cm) require a specific lawful reason to be carried.</li>
        </UL>
        <P>
          Eco-Bikes &amp; Camping Supplies Co. accepts <STRONG>no
          liability</STRONG> for illegal carry, unauthorised use, or any
          resulting harm caused by a product used outside its intended
          purpose.
        </P>
      </section>

      <section>
        <H2>3. General</H2>
        <P>
          Products are sold for their intended purpose only. Nothing in this
          disclaimer limits or excludes any legal right you have as a
          consumer that cannot be excluded under UK law. For the full
          commercial terms please see our{' '}
          <Link to="/conditions-of-sale" className="link-expand">
            Conditions of Sale
          </Link>{' '}
          and{' '}
          <Link to="/terms" className="link-expand">Terms of Service</Link>.
        </P>
      </section>
    </LegalLayout>
  );
}
